import { Formik } from 'formik';
import { useEffect, useState } from 'react';

import { CategorySelector } from './CategorySelector';

import { CloseButton } from '@components/ActionButtons/CloseButton';
import { Box } from '@components/Box';
import {
  FormButtonsContainer,
  FormSectionContainer,
} from '@components/Container';
import {
  CancelButton,
  FormikCheckbox,
  FormikSelectMultiple,
  SubmitButton,
} from '@components/Form';
import { FormikInput } from '@components/Form/FormikInput';
import { Loading } from '@components/Loading';
import { Space } from '@components/Space';
import { useCategorySelectOptions } from '@hooks/useCategorySelectOptions';
import { useNotification } from '@hooks/useNotification';
import { useSalesTaxSelectOptions } from '@hooks/useSalesTaxSelectOptions';
import { ReactSelectOption } from '@models/common/ReactSelectOption';
import {
  productDefaultValue,
  ProductResultViewModel,
  productValidation,
} from '@models/Product';
import { salesTaxDefaultValue } from '@models/SalesTax';
import { ProductService } from '@services/Product';

interface Props {
  onClose?: (
    productResultViewModel?: ProductResultViewModel,
    refreshGrid?: boolean
  ) => void;
}

export const ProductCreate: React.FC<Props> = ({ onClose }) => {
  const [isLoading, setIsLoading] = useState(true);
  const { showError, showSuccess } = useNotification();

  const {
    isLoading: isCategorySelectOptionsLoading,
    loadData: loadCategorySelectOptions,
  } = useCategorySelectOptions();

  const {
    salesTaxSelectOptions,
    isLoading: isSalesTaxSelectOptionsLoading,
    loadData: loadSalesTaxSelectOptions,
  } = useSalesTaxSelectOptions();

  const [productSalesTaxes, setProductSalesTaxes] =
    useState<ReactSelectOption[]>();

  const handleProductSalesTaxesOnChange = (
    option: readonly ReactSelectOption[]
  ) => {
    const selectedSalesTaxOptions = option.map((row) => ({
      value: row.value,
      label: row.label,
    }));
    setProductSalesTaxes(selectedSalesTaxOptions);
  };

  useEffect(() => {
    loadCategorySelectOptions();
    loadSalesTaxSelectOptions();
  }, []);

  useEffect(() => {
    if (!isCategorySelectOptionsLoading && !isSalesTaxSelectOptionsLoading) {
      setIsLoading(false);
    }
  }, [isCategorySelectOptionsLoading, isSalesTaxSelectOptionsLoading]);

  if (isLoading) {
    return <Loading />;
  } else {
    return (
      <>
        <CloseButton onClick={() => onClose && onClose()} />

        <Box size="medium">
          <Formik
            initialValues={productDefaultValue}
            validationSchema={productValidation}
            validateOnBlur={true}
            validateOnChange={true}
            onSubmit={(values, actions) => {
              if (productSalesTaxes) {
                values.productSalesTaxes = productSalesTaxes.map((row) => ({
                  id: Number(row.value),
                  productId: 0,
                  salesTaxId: parseInt(row.value.toString()),
                  salesTax: salesTaxDefaultValue,
                }));
              }

              ProductService.create(values)
                .then((result) => {
                  if (result.isSuccess && 'data' in result) {
                    showSuccess('Successfully created a record.');
                    onClose && onClose(result, true);
                  } else {
                    console.log(
                      'Error: Failed to create a record.',
                      result.errorMessages
                    );
                    showError(`${result.errorMessages}`);
                  }
                })
                .catch((error) => {
                  showError('Failed to handle the request.');
                  console.log(error);
                })
                .finally(() => {
                  actions.setSubmitting(false);
                });
            }}
          >
            {(formikProps) => {
              return (
                <form method="POST" onSubmit={formikProps.handleSubmit}>
                  <FormSectionContainer>
                    <FormikInput
                      label="Product Name"
                      name="name"
                      autoComplete="product-name"
                    />
                    <CategorySelector
                      selectedValue={formikProps.values.categoryID}
                      onValueChange={(c) =>
                        formikProps.setFieldValue('categoryID', c)
                      }
                    />
                    <FormikInput label="Description" name="description" />
                    <FormikInput label="Price" name="price" />
                    <div className="flex">
                      <FormikCheckbox label="Sell This" name="isSales" />
                      <FormikCheckbox label="Buy This" name="isPurchase" />
                    </div>

                    <FormikSelectMultiple
                      label="Sales Tax"
                      name="productSalesTaxes"
                      selection={salesTaxSelectOptions}
                      changeHandler={handleProductSalesTaxesOnChange}
                      maxMenuHeight={100}
                    />

                    <Space />
                  </FormSectionContainer>
                  <FormButtonsContainer>
                    <CancelButton onClick={() => onClose && onClose()} />

                    <SubmitButton
                      label="Save"
                      disabled={
                        formikProps.isSubmitting || !formikProps.isValid
                      }
                    />
                  </FormButtonsContainer>
                </form>
              );
            }}
          </Formik>
        </Box>
      </>
    );
  }
};
